// --- React
import { useCallback, useMemo, useState } from "react";
// --- Config & Utils
import * as columnDefs from "../../../../../components/AGGrid/config/columns-def";
// --- Hooks
import { useLicensingJobsData } from "../../../hooks/useLicensingJobsData";

/**
 * Custom hook for Licensing Jobs grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Jobs grid
 * - Fetches sorted jobs data from the API
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Optional array of column handler functions
 * @param {boolean} params.isTapAdmin - Whether the current user has TAP Admin permissions
 * @returns {object} { setSort, columns }
 */
export function useJobsGridUtils({
  columnHandlers = [],
  isTapAdmin = false,
} = {}) {
  const { fetchLicensingJobs, updateJobs, licJobRange } =
    useLicensingJobsData();

  const [sortedColId, setSortedColId] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle sorting: update state, fetch sorted data, update Jobs
  const setSort = useCallback(
    async (colId, direction) => {
      setSortedColId(colId);
      setSortDirection(direction);

      const customSort = [colId, direction.toUpperCase()];
      const data = await fetchLicensingJobs(licJobRange, null, customSort);

      if (data) {
        updateJobs({
          jobs: data?.content,
          totalItems: data?.total,
          totalPages: Math.ceil(data?.total / data?.size) || 0,
          currentPage: data?.pageNumber,
          licJobSort: customSort,
        });
      }
    },
    [fetchLicensingJobs, updateJobs, licJobRange],
  );

  // Prepare column definitions with sorting controls
  const baseColumns = columnDefs["licensingJobs"](
    ...columnHandlers,
    isTapAdmin,
  )["default"];

  const columns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        headerComponentParams: {
          ...col.headerComponentParams,
          setSort,
          sortedColId,
          sortDirection,
        },
      })),
    [setSort, sortedColId, sortDirection, baseColumns],
  );

  return { setSort, columns };
}
